/**
 * Videollamada — MVP UI con preview local + panel de captions live.
 *
 * Scope: implementa el shell UI, preview de cámara local (getUserMedia),
 * controles (mute/video-off/hang up/switch camera) y panel de captions
 * que reusa el stream de ptt:transcription del socket (Whisper).
 *
 * NOTE: La señalización WebRTC para video grupal multi-peer no está
 * implementada en este MVP — el tile grande muestra el self-preview
 * y los PIPs muestran avatares de los users en el canal. Para conectar
 * peers reales hay que añadir signaling video:offer/answer/ice equivalente
 * al flujo PTT en backend/src/socket/handlers.js.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Mic, MicOff, Video, VideoOff, PhoneOff, RotateCcw, Languages,
} from 'lucide-react';
import useStore from '../store/useStore';
import { getSocket } from '../hooks/useSocket';

const CAPTION_LIMIT = 12;

export default function VideoCallPage() {
  const navigate = useNavigate();
  const { channelId } = useParams();
  const user = useStore(s => s.user);
  const channels = useStore(s => s.channels);
  const channelUsers = useStore(s => s.channelUsers);
  const channel = useMemo(
    () => (channels || []).find(c => c.id === channelId),
    [channels, channelId]
  );
  const peers = (channelUsers[channelId] || []).filter(u => u.userId !== user?.id);

  const [localStream, setLocalStream] = useState(null);
  const [muted, setMuted] = useState(false);
  const [videoOff, setVideoOff] = useState(false);
  const [facing, setFacing] = useState('user'); // 'user' | 'environment'
  const [error, setError] = useState(null);
  const [captions, setCaptions] = useState([]);
  const [translate, setTranslate] = useState(false);
  const localVideoRef = useRef(null);

  // Acquire camera+mic on mount, re-acquire on facing change
  useEffect(() => {
    let cancelled = false;
    let stream = null;
    (async () => {
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: facing },
          audio: true,
        });
        if (cancelled) { stream.getTracks().forEach(t => t.stop()); return; }
        setLocalStream(stream);
        if (localVideoRef.current) localVideoRef.current.srcObject = stream;
      } catch (e) {
        setError(e.message || 'No se pudo acceder a la cámara/micrófono');
      }
    })();
    return () => {
      cancelled = true;
      if (stream) stream.getTracks().forEach(t => t.stop());
    };
  }, [facing]);

  // Apply mute / video-off
  useEffect(() => {
    if (!localStream) return;
    localStream.getAudioTracks().forEach(t => { t.enabled = !muted; });
    localStream.getVideoTracks().forEach(t => { t.enabled = !videoOff; });
  }, [muted, videoOff, localStream]);

  // Captions — listen to existing ptt:transcription on the socket
  useEffect(() => {
    const socket = getSocket();
    if (!socket) return;
    const onTx = ({ userId, username, channelId: ch, text, language }) => {
      if (ch !== channelId) return;
      if (!text?.trim()) return;
      setCaptions(prev => {
        const next = [...prev, {
          id: Date.now() + Math.random(),
          userId, username,
          text: text.trim(),
          language: language || 'es',
          ts: Date.now(),
        }];
        return next.slice(-CAPTION_LIMIT);
      });
    };
    socket.on('ptt:transcription', onTx);
    return () => { socket.off('ptt:transcription', onTx); };
  }, [channelId]);

  const hangup = () => {
    if (localStream) localStream.getTracks().forEach(t => t.stop());
    navigate('/');
  };

  const channelHue = '#007AFF';

  const renderCaption = (c) => {
    const display = translate && c.language !== 'en'
      ? `${c.text}  ↦  (TODO traducción)` // Real translation requires Whisper "translate" mode endpoint
      : c.text;
    return (
      <div key={c.id} className="mb-2 animate-fade-in">
        <span className="text-[10px] font-bold uppercase mr-2 px-1.5 py-0.5 rounded-sm"
          style={{ background: '#E6F1FF', color: '#007AFF' }}>{c.username}</span>
        <span className="text-[11px] text-n-500 mr-2 font-mono">
          {new Date(c.ts).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })}
        </span>
        <p className="text-[13px] text-n-800 leading-snug mt-0.5">{display}</p>
      </div>
    );
  };

  return (
    <div className="flex flex-col h-screen overflow-hidden text-white"
      style={{ background: `radial-gradient(circle at 30% 20%, ${channelHue}33, #0B0B12 65%), #0B0B12` }}
    >
      {/* Top bar */}
      <header className="flex items-center justify-between px-4 py-3 flex-shrink-0"
        style={{ background: 'rgba(0,0,0,0.30)', backdropFilter: 'blur(20px)', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
        <div>
          <div className="text-xs uppercase tracking-widest text-white/60">Videollamada</div>
          <div className="text-base font-bold">{channel?.name || 'Canal'}</div>
        </div>
        <button
          onClick={() => navigate('/')}
          className="text-xs px-3 py-1.5 rounded-full font-semibold"
          style={{ background: 'rgba(255,255,255,0.10)', color: '#fff' }}
        >
          Minimizar
        </button>
      </header>

      <div className="flex-1 grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-3 p-3 overflow-hidden">
        {/* Main stage + PIPs */}
        <div className="flex flex-col gap-3 overflow-hidden">
          <div className="flex-1 rounded-lg overflow-hidden relative"
            style={{ background: '#0B0B12', border: '1px solid rgba(255,255,255,0.08)' }}>
            {error ? (
              <div className="absolute inset-0 flex items-center justify-center text-sm text-white/70 p-6 text-center">
                {error}
              </div>
            ) : videoOff ? (
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-32 h-32 rounded-full flex items-center justify-center text-4xl font-bold"
                  style={{ background: `linear-gradient(135deg, ${user?.avatarColor || '#007AFF'}, #AF52DE)`, boxShadow: `0 20px 50px ${channelHue}66` }}>
                  {user?.username?.[0]?.toUpperCase() || '?'}
                </div>
              </div>
            ) : (
              <video ref={localVideoRef} autoPlay muted playsInline
                className="absolute inset-0 w-full h-full object-cover" />
            )}
            <div className="absolute bottom-3 left-3 px-2.5 py-1 rounded-full text-xs font-semibold"
              style={{ background: 'rgba(0,0,0,0.45)', color: '#fff' }}>
              {user?.username || 'Tú'} {muted && '· 🔇'}
            </div>
          </div>

          {/* PIPs row */}
          <div className="flex gap-2 flex-shrink-0 overflow-x-auto">
            {peers.slice(0, 4).map(p => (
              <div key={p.userId} className="w-32 h-20 rounded-md flex items-center justify-center flex-shrink-0 relative"
                style={{ background: '#1A1A22', border: '1px solid rgba(255,255,255,0.08)' }}>
                <div className="w-10 h-10 rounded-full flex items-center justify-center text-base font-bold"
                  style={{ background: `linear-gradient(135deg, ${p.avatarColor || '#007AFF'}, #AF52DE)` }}>
                  {p.username?.[0]?.toUpperCase()}
                </div>
                <div className="absolute bottom-1 left-1 text-[10px] text-white/70">{p.username}</div>
              </div>
            ))}
            {peers.length === 0 && (
              <div className="text-xs text-white/50 italic px-3 py-2">
                Sin otros participantes en {channel?.name || 'este canal'}.
              </div>
            )}
          </div>

          {/* Controls */}
          <div className="flex items-center justify-center gap-3 py-3 flex-shrink-0">
            <button onClick={() => setMuted(m => !m)}
              className="w-12 h-12 rounded-full flex items-center justify-center"
              style={{ background: muted ? '#FF3B30' : 'rgba(255,255,255,0.10)' }}>
              {muted ? <MicOff size={18} /> : <Mic size={18} />}
            </button>
            <button onClick={() => setVideoOff(v => !v)}
              className="w-12 h-12 rounded-full flex items-center justify-center"
              style={{ background: videoOff ? '#FF3B30' : 'rgba(255,255,255,0.10)' }}>
              {videoOff ? <VideoOff size={18} /> : <Video size={18} />}
            </button>
            <button onClick={() => setFacing(f => f === 'user' ? 'environment' : 'user')}
              title="Cambiar cámara"
              className="w-12 h-12 rounded-full flex items-center justify-center"
              style={{ background: 'rgba(255,255,255,0.10)' }}>
              <RotateCcw size={18} />
            </button>
            <button onClick={hangup}
              className="px-5 h-12 rounded-full flex items-center gap-2 font-bold"
              style={{ background: '#FF2D55', boxShadow: '0 8px 22px rgba(255,45,85,.45)' }}>
              <PhoneOff size={18} /> Colgar
            </button>
          </div>
        </div>

        {/* Captions panel */}
        <aside className="rounded-lg flex flex-col overflow-hidden"
          style={{ background: 'rgba(255,255,255,0.96)', color: '#1D1D1F', border: '1px solid rgba(255,255,255,0.20)' }}>
          <div className="flex items-center justify-between px-4 py-3" style={{ borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
            <div>
              <div className="text-[10px] font-bold uppercase tracking-widest text-n-500">Captions</div>
              <div className="text-sm font-bold text-n-800">Transcripción en vivo</div>
            </div>
            <button
              onClick={() => setTranslate(t => !t)}
              className="flex items-center gap-1 text-[11px] px-2 py-1 rounded-full font-semibold"
              style={translate
                ? { background: '#E6F1FF', color: '#007AFF' }
                : { background: '#F5F5F7', color: '#86868B' }}
              title="Traducir a inglés (TODO)"
            >
              <Languages size={11} />
              {translate ? 'EN' : 'ES'}
            </button>
          </div>
          <div className="flex-1 overflow-y-auto scrollbar-thin px-4 py-3">
            {captions.length === 0 ? (
              <p className="text-[12px] text-n-400 italic">Esperando voz en {channel?.name || 'el canal'}…</p>
            ) : (
              captions.map(renderCaption)
            )}
          </div>
          <div className="px-4 py-2 text-[10px] text-n-400 italic" style={{ borderTop: '1px solid rgba(0,0,0,0.04)' }}>
            Captions vienen del pipeline Whisper de PTT (post-grabación, no streaming).
            Modo translate → TODO endpoint dedicado.
          </div>
        </aside>
      </div>
    </div>
  );
}
